// node_modules
import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";

// redux
import {
  useCreateStationMutation,
  useDeleteStationMutation,
  useGetStationListQuery,
  useUpdateStationMutation,
  useUploadImageMutation,
  useUploadSoundMutation,
} from "../../../redux/services/foodStation";
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";

// context
import { ToastContext } from "../../../App";

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./Stations.module.css";

const Stations = () => {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { isMobile } = useMobile(1024);

  const [
    createStation,
    {
      isSuccess: isCreateStationSuccess,
      isError: isCreateStationError,
      error: createStationError,
    },
  ] = useCreateStationMutation();

  const dispatch = useDispatch();

  const {
    data: stations,
    isSuccess: isGetStationsSuccess,
    isError: isGetStationsError,
  } = useGetStationListQuery(getSubdomain());
  const [
    updateStation,
    {
      isSuccess: isUpdateStationSuccess,
      isError: isUpdateStationError,
      error: updateStationError,
    },
  ] = useUpdateStationMutation();
  const [
    deleteStation,
    {
      isSuccess: isDeleteStationSuccess,
      isError: isDeleteStationError,
      error: deleteStationError,
    },
  ] = useDeleteStationMutation();
  const [
    uploadImage,
    {
      isSuccess: isUploadImageSuccess,
      isError: isUploadImageError,
      error: uploadImageError,
    },
  ] = useUploadImageMutation();
  const [
    uploadSound,
    {
      isSuccess: isUploadSoundSuccess,
      isError: isUploadSoundError,
      error: uploadSoundError,
    },
  ] = useUploadSoundMutation();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showSoundModal, setShowSoundModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [id, setId] = useState(null);
  const [name, setName] = useState(null);
  const [imageStationName, setImageStationName] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentSound, setCurrentSound] = useState(null);

  useEffect(() => {
    if (isCreateStationSuccess) {
      setShowModal(false);
      showToast("success", "Station", "Station created!");
    }
  }, [isCreateStationSuccess]);

  useEffect(() => {
    if (isUpdateStationSuccess) {
      setShowModal(false);
      showToast("success", "Station", "Station updated!");
    }
  }, [isUpdateStationSuccess]);

  useEffect(() => {
    if (isUploadImageSuccess) {
      setShowImageModal(false);
      showToast("success", "Station", "Image uploaded!");
    }
  }, [isUploadImageSuccess]);

  useEffect(() => {
    if (isUploadSoundSuccess) {
      setShowSoundModal(false);
      showToast("success", "Station", "Sound uploaded!");
    }
  }, [isUploadSoundSuccess]);

  useEffect(() => {
    if (isGetStationsError)
      showToast("error", "Station", "Unable to load stations");
  }, [isGetStationsError]);

  useEffect(() => {
    if (isCreateStationError)
      showToast(
        "error",
        createStationError?.status,
        createStationError?.data?.message ||
          (createStationError?.data?.errors && "Incorrect data")
      );
  }, [isCreateStationError]);

  useEffect(() => {
    if (isUpdateStationError)
      showToast(
        "error",
        updateStationError?.status,
        updateStationError?.data?.message ||
          (updateStationError?.data?.errors && "Incorrect data")
      );
  }, [isUpdateStationError]);

  useEffect(() => {
    if (isDeleteStationSuccess) {
      showToast("success", "Station", "Station deleted!");
      setShowDeleteModal(false);
    }
  }, [isDeleteStationSuccess]);

  useEffect(() => {
    if (isDeleteStationError)
      showToast(
        "error",
        deleteStationError?.status,
        deleteStationError?.data?.message
      );
  }, [isDeleteStationError]);

  useEffect(() => {
    if (isUploadImageError)
      showToast(
        "error",
        uploadImageError?.status,
        uploadImageError?.data?.message ||
          (uploadImageError?.data?.errors && "Incorrect data")
      );
  }, [isUploadImageError]);

  useEffect(() => {
    if (isUploadSoundError)
      showToast(
        "error",
        uploadSoundError?.status,
        uploadSoundError?.data?.message ||
          (uploadSoundError?.data?.errors && "Incorrect data")
      );
  }, [isUploadSoundError]);

  useEffect(() => {
    if (isGetStationsSuccess || isGetStationsError) {
      dispatch(setLoading(false));
    }
  }, [isGetStationsSuccess, isGetStationsError]);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      title: "",
    },
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (mode === "create") {
        handleSubmit(onCreate)();
      } else {
        handleSubmit(onSaveEdit)();
      }
    }
  };

  const onCreate = (data) => {
    const payload = {
      name: data.title,
      active: data.active === "Active",
      order_restriction:
        data.restriction.length === 1
          ? `00:0${data.restriction}`
          : `00:${data.restriction}`,
    };
    createStation({ subdomain: getSubdomain(), data: payload });
  };

  const onSaveEdit = (data) => {
    updateStation({
      id,
      subdomain: getSubdomain(),
      data: {
        name: data.title,
        order_restriction:
          data.restriction.length === 1
            ? `00:0${data.restriction}`
            : `00:${data.restriction}`,
      },
    });
  };

  const openModal = (mode, rowData) => () => {
    if (mode === "create") {
      setId(null);
      setValue("title", "");
      setValue("active", "Active");
      setValue("restriction", 10);
    } else {
      setId(rowData.id);
      setValue("title", rowData.name);
      setValue(
        "restriction",
        rowData.order_restriction.split(":")[1][0] === 0
          ? rowData.order_restriction.split(":")[1][1]
          : rowData.order_restriction.split(":")[1]
      );
    }
    setShowModal(true);
    setMode(mode);
  };

  const openDeleteModal = (rowData) => () => {
    setId(rowData.id);
    setName(rowData.name);
    setShowDeleteModal(true);
  };

  const onDelete = () => {
    deleteStation({ id, subdomain: getSubdomain() });
  };

  const openImageModal = (rowData) => () => {
    setCurrentImage(`https://dwtl.food/public/images/${rowData.image_name}`);
    setImageStationName(rowData.name);
    setId(rowData.id);
    setShowImageModal(true);
  };

  const openSoundModal = (rowData) => () => {
    setCurrentSound(`https://dwtl.food/public/sounds/${rowData.sound_name}`);
    setId(rowData.id);
    setShowSoundModal(true);
  };

  const onImageSelect = (e) => {
    setCurrentImage(e.files[0].objectURL);
  };

  const onImageUpload = (e) => {
    uploadImage({ id, subdomain: getSubdomain(), data: e.files[0] });
  };

  const onSoundSelect = (e) => {
    setCurrentSound(e.files[0].objectURL);
  };

  const onSoundUpload = (e) => {
    uploadSound({ id, subdomain: getSubdomain(), data: e.files[0] });
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className={styles.cellSoundWrapper}>
        <Button
          icon="pi pi-pencil"
          className={`p-button-text ${styles.soundBtn}`}
          onClick={openModal("edit", rowData)}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-text ${styles.deleteBtn}`}
          onClick={openDeleteModal(rowData)}
        />
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div
        className={`flex justify-content-between align-items-center ${styles.header}`}
      >
        <h2>Stations</h2>
        <Button className={styles.createBtn} onClick={openModal("create")}>
          <span>ADD STATION</span> <i className="pi pi-plus" />
        </Button>
      </div>
      {stations &&
        (isMobile ? (
          <div className={styles.cardList}>
            {stations.map((station) => {
              const timeArray = station.order_restriction.split(":");

              return (
                <div key={station.id} className={styles.cardItem}>
                  <div className={styles.cardInfo}>
                    <div
                      style={{
                        backgroundImage: `url(https://dwtl.food/public/images/${station.image_name})`,
                      }}
                      className={styles.cardImage}
                      onClick={openImageModal(station)}
                    >
                      <div>
                        <i className="pi pi-pencil"></i>
                      </div>
                    </div>
                    <div className={styles.info}>
                      <div className={styles.name}>{station.name}</div>
                      <div className={styles.subfield}>
                        {timeArray[1] === "00"
                          ? `Not restricted`
                          : `${timeArray[1]} minutes`}
                      </div>
                    </div>
                  </div>
                  <div className={styles.actions}>
                    {station.active ? (
                      <button
                        className={styles.cardEditBtn}
                        onClick={() => {
                          updateStation({
                            id: station.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !station.active,
                            },
                          });
                        }}
                      >
                        <i className="pi pi-eye" />
                      </button>
                    ) : (
                      <button
                        className={styles.cardEditBtn}
                        onClick={() => {
                          updateStation({
                            id: station.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !station.active,
                            },
                          });
                        }}
                      >
                        <i className="pi pi-eye-slash" />
                      </button>
                    )}
                    <button
                      className={styles.cardEditBtn}
                      onClick={openModal("edit", station)}
                    >
                      <i className="pi pi-pencil" />
                    </button>
                    <button
                      className={styles.cardDeleteBtn}
                      onClick={openDeleteModal(station)}
                    >
                      <i className="pi pi-trash" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <DataTable value={stations} rows={6}>
            <Column
              field="name"
              header="Name"
              body={(rowData) => {
                return <div className={styles.cellInsider}>{rowData.name}</div>;
              }}
            />

            <Column
              body={(rowData) => {
                return (
                  <div className={styles.cellImageWrapper}>
                    <div
                      style={{
                        backgroundImage: `url(https://dwtl.food/public/images/${rowData.image_name})`,
                      }}
                      className={styles.imageCell}
                      onClick={openImageModal(rowData)}
                    >
                      <div>
                        <i className="pi pi-pencil"></i>
                      </div>
                    </div>
                  </div>
                );
              }}
              header="Image"
              style={{ width: "198px" }}
            />

            <Column
              body={(rowData) => {
                return (
                  <div className={styles.cellSoundWrapper}>
                    <Button
                      icon="pi pi-play"
                      className={`p-button-text ${styles.playBtn}`}
                      disabled={!rowData.sound_name}
                      onClick={() => {
                        const audio = document.getElementById(
                          rowData.sound_name
                        );
                        audio.play();
                      }}
                    />
                    <audio
                      id={rowData.sound_name}
                      style={{ visibility: "hidden", position: "absolute" }}
                      controls={true}
                      src={`https://dwtl.food/public/sounds/${rowData.sound_name}`}
                    />
                    <Button
                      icon="pi pi-pencil"
                      className={`p-button-text ${styles.soundBtn}`}
                      onClick={openSoundModal(rowData)}
                    />
                  </div>
                );
              }}
              header="Sound"
              style={{ width: "175px" }}
            />
            <Column
              body={(rowData) => {
                const timeArray = rowData.order_restriction.split(":");

                return (
                  <div className={styles.cellInsider}>
                    {timeArray[1] === "00"
                      ? `Not restricted`
                      : `${timeArray[1]} minutes`}
                  </div>
                );
              }}
              field="order_restriction"
              header="Order Frequency"
            />
            <Column
              body={(rowData) => {
                return (
                  <>
                    {rowData.active ? (
                      <Button
                        icon="pi pi-eye"
                        className={`p-button-text ${styles.activeBtn}`}
                        onClick={() => {
                          updateStation({
                            id: rowData.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !rowData.active,
                            },
                          });
                        }}
                      />
                    ) : (
                      <Button
                        icon="pi pi-eye-slash"
                        className={`p-button-text ${styles.activeBtn}`}
                        onClick={() => {
                          updateStation({
                            id: rowData.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !rowData.active,
                            },
                          });
                        }}
                      />
                    )}
                  </>
                );
              }}
              style={{ width: "130px" }}
              header="Active"
            />
            <Column
              body={actionBodyTemplate}
              header="Actions"
              style={{ width: "175px" }}
            />
          </DataTable>
        ))}

      <Dialog
        header={`${mode === "create" ? "New" : "Edit"} Food Station`}
        visible={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Save"
              type="submit"
              className={`p-button-success ${styles.saveBtn}`}
              onClick={
                mode === "create"
                  ? handleSubmit(onCreate)
                  : handleSubmit(onSaveEdit)
              }
            />
          </div>
        )}
        className={`p-fluid ${styles.stationModal}`}
      >
        <form>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <div className="field">
                <label>Title</label>
                <InputText
                  className={`w-full mb-3 ${styles.input}`}
                  onKeyDown={handleKeyDown}
                  placeholder="Title"
                  {...field}
                />
              </div>
            )}
          />

          <Controller
            name="restriction"
            control={control}
            render={({ field }) => (
              <div className="field">
                <label>Order Frequency (minutes)</label>
                <InputMask
                  mask="9?9"
                  slotChar="  "
                  className={`w-full mb-3 ${styles.input}`}
                  onKeyDown={handleKeyDown}
                  {...field}
                />
              </div>
            )}
          />

          {mode === "create" && (
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <SelectButton
                  {...field}
                  options={["Active", "Inactive"]}
                  allowEmpty={false}
                  className={`${styles.selectButton}`}
                />
              )}
            />
          )}
        </form>
      </Dialog>

      {/* Delete modal */}
      <Dialog
        closable={false}
        header={
          <div style={{ width: "100%", textAlign: "center" }}>
            Are You Sure?
          </div>
        }
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={onDelete}
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>
            You want to delete the food station <strong>{name}</strong>.
          </span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>

      {/* Image modal */}
      <Dialog
        header={`Food Station Image`}
        visible={showImageModal}
        onHide={() => {
          setShowImageModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <FileUpload
              mode="basic"
              onSelect={onImageSelect}
              customUpload
              uploadHandler={onImageUpload}
              onUpload={() => {}}
              accept="image/*"
              selectedFileLabel="Upload"
              className={styles.uploadBtn}
            />
          </div>
        )}
        className={`p-fluid ${styles.imageModal}`}
      >
        <form>
          <div className={styles.preview}>
            <div className={styles.cardWrapper}>
              <div className={styles.stationCard}>
                <div
                  style={{
                    backgroundImage: `url(${currentImage})`,
                  }}
                  className={styles.image}
                ></div>
                <div className={styles.titleWrapper}>
                  <div>{imageStationName}</div>
                </div>
              </div>
            </div>
          </div>
          <p>Recommended file size is 2MB and dimension 720x480</p>
        </form>
      </Dialog>

      {/* Sound modal */}
      <Dialog
        header={`Edit New Order Sound`}
        visible={showSoundModal}
        onHide={() => {
          setShowSoundModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <FileUpload
              mode="basic"
              onSelect={onSoundSelect}
              customUpload
              uploadHandler={onSoundUpload}
              onUpload={() => {}}
              selectedFileLabel="Upload"
              className={styles.uploadBtn}
            />
          </div>
        )}
        className={`p-fluid ${styles.soundModal}`}
      >
        <form>
          <div className={styles.preview}>
            {currentSound && <audio controls src={currentSound}></audio>}
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default Stations;
